import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { DataTable, EmptyState, ErrorNotice, Panel, Pill, SearchInput } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { filterRows } from '@/lib/facet-table';
import { useUrlSearch } from '@/lib/use-list-query-state';
import { eventStageTone } from '@/lib/event-status';
import { readEventFamily, type FamilyMember } from '@/lib/events';

/**
 * "9466-A-BC" → "Site A"; "9614.2-BC" → "Amendment 2"; the parent → "Parent".
 *
 * @param member - One family member.
 * @returns The role of this member in the family.
 */
export function familyRole(member: Pick<FamilyMember, 'site' | 'amendment'>): string {
  const parts = [member.site ? `Site ${member.site}` : 'Parent'];
  if (member.amendment !== null) parts.push(`amendment ${member.amendment}`);
  return parts.join(', ');
}

/** A family member with the text its row shows, for the search. */
interface FamilyRow extends FamilyMember {
  readonly role: string;
  readonly place: string;
}

/** The row fields the search box matches. */
const FAMILY_SEARCH_FIELDS: ReadonlyArray<keyof FamilyRow> = ['contractNumber', 'role', 'place', 'leadFacilitatorName', 'stageName', 'programName'];

/** Its own URL key: the event page has other searchable lists. */
const FAMILY_SEARCH_KEY = 'familyQ';

function memberDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/**
 * The other events booked under this event's contract number: sibling
 * sites on the same day with their own facilitators, and amendments.
 * Renders nothing when the event stands alone.
 *
 * @param props - The current event's id.
 * @returns The panel, or null.
 */
export function ContractFamilyPanel({ eventId }: { readonly eventId: string }): React.ReactElement | null {
  const { search, setSearch } = useUrlSearch(FAMILY_SEARCH_KEY);
  const family = useQuery({
    queryKey: ['event-family', eventId],
    queryFn: ({ signal }) => readEventFamily(eventId, signal),
  });

  // Nothing while it loads: most events stand alone, and a loading line at
  // the top of the overview that then vanishes would shove the page about.
  if (family.isPending) return null;
  if (family.isError) return <ErrorNotice inline title="Related contracts unavailable" message={errorMessage(family.error)} />;
  if (family.data.length <= 1) return null;

  const base = family.data[0]?.contractNumber?.split(/[.-]/)[0] ?? '';
  const rows = filterRows<FamilyRow>(
    family.data.map((member) => ({ ...member, role: familyRole(member), place: [member.city, member.state].filter(Boolean).join(', ') })),
    search,
    FAMILY_SEARCH_FIELDS,
  );

  return (
    <Panel className="p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-portal-micro font-extrabold tracking-[1px] text-portal-muted uppercase">
          Contract family · {base}
        </h2>
        <Link to={`/events?family=${encodeURIComponent(base)}&state=all`} className="text-portal-small text-brand hover:underline">
          All {family.data.length} in the list
        </Link>
      </div>
      <SearchInput
        label="Search the contract family"
        placeholder="Contract, role, place, lead, stage or program"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        className="mb-3 w-full max-w-sm"
      />
      {rows.length === 0 ? (
        <EmptyState title="No contracts match" message="Try a different search." />
      ) : (
        <DataTable
          headers={['Contract', 'Role', 'Date', 'Time', 'Location', 'Lead', 'Participants', 'Stage']}
          rightAlignedHeaders={[6]}
          flush
          minWidth={760}
        >
          {rows.map((member) => {
            const current = member._id === eventId;
            return (
              <tr key={member._id} className={current ? 'border-t border-portal-line bg-admin-subtle align-middle' : 'border-t border-portal-line align-middle'}>
                <td className="p-3 text-portal-small whitespace-nowrap">
                  {current ? (
                    <span className="font-bold">{member.contractNumber}</span>
                  ) : (
                    <Link to={`/events/${member._id}`} className="font-bold text-brand hover:underline">
                      {member.contractNumber}
                    </Link>
                  )}
                  {current ? <span className="ml-2 text-portal-micro text-portal-muted">this event</span> : null}
                </td>
                <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{member.role}</td>
                <td className="p-3 text-portal-small whitespace-nowrap">{memberDate(member.eventDate)}</td>
                <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">
                  {[member.timeStartString, member.timeEndString].filter(Boolean).join(' – ')}
                  {member.timeZone ? ` ${member.timeZone}` : ''}
                </td>
                <td className="p-3 text-portal-small">{member.place || '—'}</td>
                <td className="p-3 text-portal-small">
                  {member.leadFacilitatorId && member.leadFacilitatorName ? (
                    <Link to={`/people/${member.leadFacilitatorId}`} className="text-brand hover:underline">
                      {member.leadFacilitatorName}
                    </Link>
                  ) : (
                    member.leadFacilitatorName || '—'
                  )}
                </td>
                <td className="p-3 text-portal-small text-right whitespace-nowrap">{member.participants ?? '—'}</td>
                <td className="p-3 text-portal-small">
                  {member.stageName ? <Pill tone={eventStageTone(member.stageName)}>{member.stageName}</Pill> : null}
                </td>
              </tr>
            );
          })}
        </DataTable>
      )}
    </Panel>
  );
}
